/**
 * AutoMazen Contact Form Section JavaScript
 * 
 * Features:
 * - Form validation with helpful error messages
 * - Input field focus/blur effects
 * - Card hover animations
 * - CSS variable initialization from section settings
 */

// Initialize Contact Form functionality with a self-executing function to avoid global scope pollution
(function() {
  const selectors = {
    section: '[data-section-type="contact-form"]',
    form: '.automazen-contact-form__form',
    formInputs: '.automazen-contact-form__input, .automazen-contact-form__textarea',
    infoCard: '.automazen-contact-form__info-card',
    formCard: '.automazen-contact-form__form-card',
    submitButton: '.automazen-contact-form__button'
  };

  class AutoMazenContactForm {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.form = this.container.querySelector(selectors.form);
      this.inputs = this.container.querySelectorAll(selectors.formInputs);
      this.infoCard = this.container.querySelector(selectors.infoCard);
      this.formCard = this.container.querySelector(selectors.formCard);
      this.submitButton = this.container.querySelector(selectors.submitButton);
      
      this.init();
    }

    init() {
      // Initialize form input effects
      this.initFormInputs();
      
      // Initialize form validation
      this.initFormValidation();
      
      // Initialize CSS variables from section settings
      this.initCssVariables();
      
      // Remove inline hover effects (replaced by CSS)
      this.removeInlineHoverEffects();
    }

    initFormInputs() {
      // Add event listeners to form inputs
      this.inputs.forEach(input => {
        // Initial check for value
        if (input.value.trim() !== '') {
          input.classList.add('has-value');
        }
        
        // Focus event
        input.addEventListener('focus', () => {
          input.classList.add('is-focused');
        });
        
        // Blur event
        input.addEventListener('blur', () => {
          input.classList.remove('is-focused');
          
          // Add 'has-value' class if input has value
          if (input.value.trim() !== '') {
            input.classList.add('has-value');
          } else {
            input.classList.remove('has-value');
          }
        });
        
        // Input event for real-time validation
        input.addEventListener('input', () => {
          this.validateInput(input);
        });
      });
    }

    initFormValidation() {
      if (!this.form) return;
      
      this.form.addEventListener('submit', (event) => {
        let isValid = true;
        
        // Validate all inputs
        this.inputs.forEach(input => {
          if (input.hasAttribute('required') && !this.validateInput(input)) {
            isValid = false;
          }
        });
        
        // Prevent form submission if not valid
        if (!isValid) {
          event.preventDefault();
        }
      });
    }

    validateInput(input) {
      // Skip validation if input is not required
      if (!input.hasAttribute('required')) return true;
      
      const value = input.value.trim();
      let isValid = true;
      
      // Check if input is empty
      if (value === '') {
        this.setInputError(input, 'This field is required');
        isValid = false;
      } 
      // Email validation
      else if (input.type === 'email' && !this.isValidEmail(value)) {
        this.setInputError(input, 'Please enter a valid email address');
        isValid = false;
      } else {
        this.clearInputError(input);
      }
      
      return isValid;
    }

    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    }

    setInputError(input, errorMessage) {
      // Remove existing error message
      this.clearInputError(input);
      
      // Add error class to input
      input.classList.add('has-error');
      
      // Create error message element
      const errorElement = document.createElement('div');
      errorElement.classList.add('automazen-contact-form__input-error');
      errorElement.textContent = errorMessage;
      
      // Insert error message after input
      input.parentNode.insertBefore(errorElement, input.nextSibling);
    }

    clearInputError(input) {
      // Remove error class from input
      input.classList.remove('has-error');
      
      // Remove error message element
      const errorElement = input.nextElementSibling;
      if (errorElement && errorElement.classList.contains('automazen-contact-form__input-error')) {
        errorElement.remove();
      }
    }

    initCssVariables() {
      // Get theme settings from data attributes or CSS variables
      const style = getComputedStyle(this.container);
      const backgroundColor = this.container.dataset.backgroundColor || style.getPropertyValue('--background-color');
      const textColor = this.container.dataset.textColor || style.getPropertyValue('--text-color');
      const formColor = this.container.dataset.formColor || style.getPropertyValue('--form-element-color') || '#98CA3F';
      const paddingTop = this.container.dataset.paddingTop || style.getPropertyValue('--padding-top');
      const paddingBottom = this.container.dataset.paddingBottom || style.getPropertyValue('--padding-bottom');
      
      // Calculate darker form color for button hover/active states
      const formColorDark = this.adjustColorBrightness(formColor, -20);
      
      // Extract RGB values for form color (for rgba calculations)
      const formColorRGB = this.hexToRgb(formColor);
      
      // Apply CSS variables to the container
      this.container.style.setProperty('--background-color', backgroundColor);
      this.container.style.setProperty('--text-color', textColor);
      this.container.style.setProperty('--form-element-color', formColor);
      this.container.style.setProperty('--form-element-color-dark', formColorDark);
      this.container.style.setProperty('--form-element-color-rgb', formColorRGB);
      this.container.style.setProperty('--subtitle-color', this.hexToRgba(textColor, 0.8));
      this.container.style.setProperty('--padding-top', `${paddingTop}px`);
      this.container.style.setProperty('--padding-bottom', `${paddingBottom}px`);
      
      // Apply form label font size
      const formLabelFontSize = this.container.querySelector('.automazen-contact-form__label')?.style.fontSize || '16px';
      this.container.style.setProperty('--form-label-font-size', formLabelFontSize);
      
      // Add responsive event listener
      this.handleResponsiveChanges();
      window.addEventListener('resize', this.handleResponsiveChanges.bind(this));
    }

    hexToRgba(hex, alpha) {
      // Convert hex color to rgba
      if (!hex) return `rgba(51, 51, 51, ${alpha})`;
      
      // Remove # if present
      hex = hex.replace('#', '');
      
      // Convert 3-digit hex to 6-digit
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      
      // Convert hex to rgb
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    
    hexToRgb(hex) {
      // Convert hex color to rgb string (for CSS variables)
      if (!hex) return '152, 202, 63';
      
      // Remove # if present
      hex = hex.replace('#', '');
      
      // Convert 3-digit hex to 6-digit
      if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      }
      
      // Convert hex to rgb
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      
      return `${r}, ${g}, ${b}`;
    }

    adjustColorBrightness(hex, percent) {
      // Convert hex to RGB
      let r = parseInt(hex.substring(1, 3), 16);
      let g = parseInt(hex.substring(3, 5), 16);
      let b = parseInt(hex.substring(5, 7), 16);
      
      // Adjust brightness
      r = Math.max(0, Math.min(255, r + (percent / 100) * 255));
      g = Math.max(0, Math.min(255, g + (percent / 100) * 255));
      b = Math.max(0, Math.min(255, b + (percent / 100) * 255));
      
      // Convert back to hex
      return '#' + ((1 << 24) + (Math.round(r) << 16) + (Math.round(g) << 8) + Math.round(b)).toString(16).slice(1);
    }

    handleResponsiveChanges() {
      // Check if we're on mobile
      const isMobile = window.innerWidth <= 767;
      
      // Get title and subtitle elements
      const title = this.container.querySelector('.automazen-contact-form__title');
      const subtitle = this.container.querySelector('.automazen-contact-form__subtitle');
      
      if (isMobile) {
        // Apply mobile styles directly to the elements
        if (title) {
          const mobileFontSize = title.dataset.mobileFontSize || '32';
          const mobilePaddingTop = title.dataset.mobilePaddingTop || '0';
          const mobilePaddingRight = title.dataset.mobilePaddingRight || '0';
          const mobilePaddingBottom = title.dataset.mobilePaddingBottom || '8';
          const mobilePaddingLeft = title.dataset.mobilePaddingLeft || '0';
          
          title.style.fontSize = `${mobileFontSize}px`;
          title.style.padding = `${mobilePaddingTop}px ${mobilePaddingRight}px ${mobilePaddingBottom}px ${mobilePaddingLeft}px`;
        }
        
        if (subtitle) {
          const mobileFontSize = subtitle.dataset.mobileFontSize || '16';
          const mobilePaddingTop = subtitle.dataset.mobilePaddingTop || '0';
          const mobilePaddingRight = subtitle.dataset.mobilePaddingRight || '0';
          const mobilePaddingBottom = subtitle.dataset.mobilePaddingBottom || '30';
          const mobilePaddingLeft = subtitle.dataset.mobilePaddingLeft || '0';
          
          subtitle.style.fontSize = `${mobileFontSize}px`;
          subtitle.style.padding = `${mobilePaddingTop}px ${mobilePaddingRight}px ${mobilePaddingBottom}px ${mobilePaddingLeft}px`;
        }
      } else {
        // Restore desktop styles from inline styles (already set in the HTML)
        if (title) {
          // Desktop styles were set directly in the HTML
          // No need to change them here
        }
        
        if (subtitle) {
          // Desktop styles were set directly in the HTML
          // No need to change them here
        }
      }
    }
    
    removeInlineHoverEffects() {
      // Remove inline hover effects from info card and form card
      if (this.infoCard) {
        this.infoCard.removeAttribute('onmouseover');
        this.infoCard.removeAttribute('onmouseout');
      }
      
      if (this.formCard) {
        this.formCard.removeAttribute('onmouseover');
        this.formCard.removeAttribute('onmouseout');
      }
    }
  }

  // Initialize all contact form sections on the page
  function initContactFormSections() {
    const contactFormSections = document.querySelectorAll(selectors.section);
    
    contactFormSections.forEach((section) => {
      new AutoMazenContactForm(section);
    });
  }

  // Initialize on DOM content loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactFormSections);
  } else {
    initContactFormSections();
  }

  // Handle Shopify section load events for the theme editor
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(selectors.section);
    if (section) {
      new AutoMazenContactForm(section);
    }
  });
})();
